import { useEffect, useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Sidebar, MobileSidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { PlatformTopbar } from "./PlatformTopbar";
import { CommandPalette } from "./CommandPalette";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtime } from "@/hooks/useRealtime";
import { api } from "@/lib/api";
import { toast } from "sonner";

import Breadcrumb from "@/components/Breadcrumb";
import SisiFloatingButton from "@/components/sisi/SisiFloatingButton";
import OverdueBillingModal from "@/components/OverdueBillingModal";
/* BACK_TO_FEDERAL_DASHBOARD_ROUTES_START */
const FEDERAL_DASHBOARD_CARD_ROUTES = [
  "/app/serpro/sitfis",
  "/app/simples-nacional",
  "/app/dctfweb",
  "/app/dashboard-federal/pgdas",
  "/app/dashboard-federal/divida-ativa",
  "/app/procuradores",
  "/app/configuracoes",
];
/* BACK_TO_FEDERAL_DASHBOARD_ROUTES_END */

export function AppLayout() {

  /* BACK_TO_FEDERAL_DASHBOARD_STATE_START */
  const location = useLocation();

  const normalizedPath =
    location.pathname.replace(/\/+$/, "") || "/";

  const showFederalDashboardBack =
    normalizedPath !== "/app/dashboard-federal" &&
    (
      FEDERAL_DASHBOARD_CARD_ROUTES.some(
        (route) =>
          normalizedPath === route ||
          normalizedPath.startsWith(`${route}/`),
      ) ||
      (
        normalizedPath === "/app/configuracoes" &&
        new URLSearchParams(location.search).get("tab") ===
          "certificados"
      )
    );
  /* BACK_TO_FEDERAL_DASHBOARD_STATE_END */
  const [openCommand, setOpenCommand] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [org, setOrg] = useState(null);
  const [cnpjUsage, setCnpjUsage] = useState(null);

  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [overdueModalOpen, setOverdueModalOpen] = useState(false);

  const { user } = useAuth();

  useRealtime();

  useEffect(() => {
    if (!user) return;

    if (user.role === "platform_owner") {
      setOverdueInvoices([]);
      setOverdueModalOpen(false);
      return;
    }

    let cancelled = false;

    async function loadOverdueBilling() {
      try {
        const { data } = await api.get(
          "/subscription/invoices",
          {
            params: {
              limit: 100,
            },
          }
        );

        const items = Array.isArray(data)
          ? data
          : data?.items || [];

        const today = new Date();
        today.setHours(
          0,
          0,
          0,
          0
        );

        const overdue = items.filter(
          (invoice) => {
            const status = String(
              invoice?.status || ""
            ).toLowerCase();

            if (
              [
                "paid",
                "cancelled",
                "canceled",
                "void",
              ].includes(status)
            ) {
              return false;
            }

            if (status === "overdue") {
              return true;
            }

            if (!invoice?.due_date) {
              return false;
            }

            const dueDate = new Date(
              `${String(invoice.due_date).slice(0, 10)}T00:00:00`
            );

            if (
              Number.isNaN(
                dueDate.getTime()
              )
            ) {
              return false;
            }

            return dueDate < today;
          }
        );

        if (cancelled) return;

        setOverdueInvoices(
          overdue
        );

        setOverdueModalOpen(
          overdue.length > 0
        );
      } catch {
        if (cancelled) return;

        setOverdueInvoices([]);
        setOverdueModalOpen(false);
      }
    }

    loadOverdueBilling();

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    if (user.role === "platform_owner") return; // platform_owner não tem escritório
    api.get("/organization").then((r) => setOrg(r.data)).catch(() => {});
    api.get("/organization/cnpj-usage").then((r) => setCnpjUsage(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    // Global toasts on important realtime events
    const onNewAlert = (e) => {
      const p = e.detail || {};
      if (p.severity === "critical") {
        toast.error(p.title || "Novo alerta crítico", {
          description: p.company_name,
        });
      } else if (p.severity === "warning") {
        toast.warning(p.title || "Novo alerta", {
          description: p.company_name,
        });
      }
    };
    window.addEventListener("simpler:alert.created", onNewAlert);
    return () => window.removeEventListener("simpler:alert.created", onNewAlert);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpenCommand((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground flex" data-testid="app-layout">
      <Sidebar orgName={org?.name} cnpjUsage={cnpjUsage} />
      <MobileSidebar
        open={mobileOpen} setOpen={setMobileOpen}
        orgName={org?.name} cnpjUsage={cnpjUsage}
      />
<div className="flex-1 flex flex-col min-w-0 min-h-screen">
  {user?.role === "platform_owner" ? (
    <PlatformTopbar onOpenMobileMenu={() => setMobileOpen(true)} />
  ) : (
  <Topbar
    onOpenCommand={() => setOpenCommand(true)}
    onOpenMobileMenu={() => setMobileOpen(true)}
    org={org}
  />
  )}

  <main className="flex-1 p-3 md:p-5 max-w-[1600px] w-full mx-auto">
    {user?.role !== "platform_owner" && <Breadcrumb />}
    <Outlet context={{ org }} />
  </main>

  <footer className="border-t border-border bg-background px-6 py-3 text-[11px] text-left text-muted-foreground">
    <span>
      Copyright © {new Date().getFullYear()} Simpler - Todos os direitos reservados.{" "}
    </span>

    <a
      href="/legal/Termos_de_Uso_Simpler.pdf"
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:underline"
    >
      Termo de Uso
    </a>

    <span> do sistema.</span>
  </footer>
</div>

      {user?.role !== "platform_owner" && <SisiFloatingButton />}

      <OverdueBillingModal
        open={overdueModalOpen}
        invoices={overdueInvoices}
        onClose={() =>
          setOverdueModalOpen(false)
        }
      />

      {user?.role !== "platform_owner" && (
      <CommandPalette
        open={openCommand}
        setOpen={setOpenCommand}
      />
      )}
    </div>
  );
}